import { useState, useEffect, useCallback, createContext, useContext, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
}

const RECOVERY_FLAG = "password-recovery-authorized";
const WELCOME_SENT_PREFIX = "welcome-email-sent-";

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const mounted = useRef(true);
  const initialised = useRef(false);

  const checkAdminRole = useCallback(async (userId: string) => {
    try {
      const { data } = await supabase.rpc("has_role", {
        _user_id: userId,
        _role: "admin",
      });
      if (mounted.current) setIsAdmin(!!data);
    } catch {
      if (mounted.current) setIsAdmin(false);
    }
  }, []);

  const sendWelcomeEmail = useCallback(async (userId: string) => {
    const key = WELCOME_SENT_PREFIX + userId;
    if (localStorage.getItem(key)) return;
    try {
      // Check server-side flag first to avoid unnecessary edge function calls
      const { data: profile } = await supabase
        .from("profiles")
        .select("welcome_sent_at")
        .eq("user_id", userId)
        .maybeSingle();
      if (profile?.welcome_sent_at) {
        localStorage.setItem(key, "1");
        return;
      }
      const { error } = await supabase.functions.invoke("send-welcome");
      if (!error) {
        localStorage.setItem(key, "1");
      } else {
        console.error("Welcome email failed:", error);
      }
    } catch (e) {
      console.error("Welcome email failed:", e);
    }
  }, []);

  const handleSession = useCallback(
    (newSession: Session | null, event?: string, previousEmail?: string) => {
      if (!mounted.current) return;

      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (newSession?.user) {
        // Fire admin check without blocking loading state
        checkAdminRole(newSession.user.id);

        if (event === "SIGNED_IN") {
          sendWelcomeEmail(newSession.user.id);
        }

        // Detect email change on USER_UPDATED
        if (event === "USER_UPDATED" && previousEmail && previousEmail !== newSession.user.email) {
          supabase.functions.invoke("notify-profile-change", {
            body: {
              type: "email_changed",
              old_email: previousEmail,
              new_email: newSession.user.email,
              user_agent: navigator.userAgent,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          });
        }
      } else {
        setIsAdmin(false);
      }

      // Always set loading false after processing session
      setLoading(false);
    },
    [checkAdminRole, sendWelcomeEmail]
  );

  useEffect(() => {
    mounted.current = true;

    // 1. Restore session from storage first — this is the primary init path
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      if (!mounted.current) return;
      // Only use getSession result if onAuthStateChange hasn't fired yet
      if (!initialised.current) {
        initialised.current = true;
        handleSession(currentSession);
      }
    });

    // 2. Listen for subsequent auth changes (sign in/out, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted.current) return;

      if (event === "PASSWORD_RECOVERY") {
        sessionStorage.setItem(RECOVERY_FLAG, String(Date.now()));
      }
      if (event === "SIGNED_OUT") {
        sessionStorage.removeItem(RECOVERY_FLAG);
      }

      // Capture previous email before updating state for email change detection
      const prevEmail = user?.email ?? undefined;

      initialised.current = true;
      handleSession(newSession, event, prevEmail);
    });

    // 3. Safety timeout — use ref to avoid stale closure over `loading`
    const timeout = setTimeout(() => {
      if (mounted.current && !initialised.current) {
        console.warn("Auth loading timeout — forcing ready state");
        initialised.current = true;
        setLoading(false);
      }
    }, 5000);

    return () => {
      mounted.current = false;
      initialised.current = false;
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signOut = useCallback(async () => {
    sessionStorage.removeItem(RECOVERY_FLAG);
    await supabase.auth.signOut();
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
